import { useState, useEffect, useMemo, createContext, useContext, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { iconCache } from '@/services/iconCache';
import type { TradingCoin } from '@shared/api';

interface CryptoIconProps {
  coin: Partial<TradingCoin>;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  loading?: 'lazy' | 'eager';
  showLoadingState?: boolean;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12'
};

// Loading skeleton component
function IconSkeleton({ size, className }: { size: keyof typeof sizeClasses; className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-full bg-card-bg-medium flex items-center justify-center',
        sizeClasses[size],
        className
      )}
    >
      <div className="h-1/2 w-1/2 rounded-full bg-card-bg-light" />
    </div>
  );
}

// Placeholder component with symbol
function IconPlaceholder({
  symbol,
  name,
  size,
  className
}: {
  symbol?: string;
  name?: string;
  size: keyof typeof sizeClasses;
  className?: string;
}) {
  const displayText = symbol ? symbol.slice(0, 3) : name ? name.slice(0, 3) : '?';

  return (
    <div
      className={cn(
        'rounded-full bg-card-bg-light border border-card-bg-medium flex items-center justify-center',
        sizeClasses[size],
        className
      )}
    >
      <span
        className={cn(
          'text-white font-semibold select-none',
          size === 'sm' && 'text-[8px]',
          size === 'md' && 'text-[10px]',
          size === 'lg' && 'text-xs',
          size === 'xl' && 'text-sm'
        )}
      >
        {displayText.toUpperCase()}
      </span>
    </div>
  );
}

export function CryptoIcon({
  coin,
  size = 'md',
  className,
  loading = 'lazy',
  showLoadingState = true
}: CryptoIconProps) {
  const [iconSrc, setIconSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Context available but not currently used in this component

  // Validate coin data
  const isValidCoin = useMemo(() => {
    return coin.symbol && coin.image_url && typeof coin.image_url === 'string';
  }, [coin.symbol, coin.image_url]);

  // Load icon from cache
  useEffect(() => {
    if (!isValidCoin) {
      setError('Invalid coin data');
      return;
    }

    const loadIcon = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if already cached
        if (iconCache.isCached(coin as TradingCoin)) {
          const cachedSrc = await iconCache.getIcon(coin as TradingCoin);
          setIconSrc(cachedSrc);
          return;
        }

        // Load and cache if not available
        const src = await iconCache.getIcon(coin as TradingCoin);
        setIconSrc(src);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load icon';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    // Lazy loading implementation
    if (loading === 'lazy') {
      const timer = setTimeout(loadIcon, 50); // Small delay for lazy loading
      return () => clearTimeout(timer);
    } else {
      void loadIcon();
    }
  }, [coin, isValidCoin, loading]);

  // Show loading state
  if (isLoading && showLoadingState) {
    return <IconSkeleton size={size} className={className} />;
  }

  // Show placeholder for invalid coins or errors
  if (!isValidCoin || error) {
    return (
      <IconPlaceholder
        symbol={coin.symbol}
        name={coin.name}
        size={size}
        className={className}
      />
    );
  }

  // Show cached icon
  if (iconSrc) {
    return (
      <img
        src={iconSrc}
        alt={`${coin.name || coin.symbol} cryptocurrency icon`}
        className={cn(
          'rounded-full object-cover flex-shrink-0',
          sizeClasses[size],
          className
        )}
        onError={() => {
          setError('Display error');
        }}
        loading={loading === 'lazy' ? 'lazy' : 'eager'}
      />
    );
  }

  // Fallback placeholder while loading
  return (
    <IconPlaceholder
      symbol={coin.symbol}
      name={coin.name}
      size={size}
      className={className}
    />
  );
}

// Context for managing icon cache and preloading
interface CryptoIconContextType {
  preloadIcons: (coins: TradingCoin[]) => Promise<void>;
  isPreloading: boolean;
  cacheStats: () => ReturnType<typeof iconCache.getStats>;
  clearCache: () => void;
}

const CryptoIconContext = createContext<CryptoIconContextType | undefined>(undefined);

export function CryptoIconProvider({ children }: { children: ReactNode }) {
  const [isPreloading, setIsPreloading] = useState(false);

  const preloadIcons = async (coins: TradingCoin[]) => {
    if (isPreloading || coins.length === 0) return;

    try {
      setIsPreloading(true);
      await iconCache.preloadIcons(coins);
    } catch {
      // Silent fail - icons will load on demand
    } finally {
      setIsPreloading(false);
    }
  };

  const cacheStats = () => iconCache.getStats();
  const clearCache = () => iconCache.clear();

  return (
    <CryptoIconContext.Provider
      value={{
        preloadIcons,
        isPreloading,
        cacheStats,
        clearCache
      }}
    >
      {children}
    </CryptoIconContext.Provider>
  );
}

export function useCryptoIconContext() {
  const context = useContext(CryptoIconContext);
  if (context === undefined) {
    throw new Error('useCryptoIconContext must be used within a CryptoIconProvider');
  }
  return context;
}

// Hook for icon performance monitoring
export function useCryptoIconStats() {
  const { cacheStats, clearCache } = useCryptoIconContext();

  const [stats, setStats] = useState(() => cacheStats());

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(cacheStats());
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [cacheStats]);

  return {
    stats,
    clearCache,
    refreshStats: () => setStats(cacheStats())
  };
}

// Hook for batch icon loading with progress tracking
export function useCryptoIconBatch(coins: TradingCoin[]) {
  const { isPreloading } = useCryptoIconContext();
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });

  const loadBatch = async () => {
    if (coins.length === 0) return;

    setProgress({ loaded: 0, total: coins.length });

    // Load icons and track progress
    let loaded = 0;
    for (const coin of coins) {
      try {
        await iconCache.getIcon(coin);
        loaded++;
        setProgress({ loaded, total: coins.length });
      } catch (error) {
      }
    }
  };

  return {
    loadBatch,
    isLoading: isPreloading,
    progress: progress.total > 0 ? (progress.loaded / progress.total) * 100 : 0,
    loadedCount: progress.loaded,
    totalCount: progress.total
  };
}